import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { ApiError, invitesApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { queryKeys } from "../../queryKeys";
import type {
  InviteForm,
  InviteFormConfig,
  InviteFormPatch,
  InviteQuestion,
  InviteRequest,
  InviteRequestList,
  InviteStatus,
} from "../../types/invite";
import "./EventInvitesTab.css";

const STATUS_LABEL: Record<InviteStatus, string> = {
  pending: "Pending review",
  approved: "Awaiting payment",
  paid: "Paid",
  waitlisted: "Waitlisted",
  expired: "Expired",
};

function formatWhen(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function waLink(phone: string, text?: string): string {
  const digits = phone.replace(/\D/g, "");
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
}

/** The message that fits where the request stands, for sending by hand on WhatsApp. */
function messageFor(req: InviteRequest): string | undefined {
  if (req.status === "approved") {
    const reminderDue = req.approved_at && Date.now() - new Date(req.approved_at).getTime() > 12 * 3600 * 1000;
    return reminderDue ? req.messages.reminder : req.messages.approved;
  }
  if (req.status === "waitlisted") return req.messages.waitlisted;
  return undefined;
}

async function copy(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Couldn't copy. Long-press to copy instead.");
  }
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

type SettingsDraft = {
  enabled: boolean;
  capacity_heads: string;
  capacity_invites: string;
  hold_hours: string;
  reminder_hours: string;
  application_prefix: string;
  solo_tier_id: string;
  couple_tier_id: string;
  closes_at_ist: string;
  config: InviteFormConfig;
  details: string;
  confirmations: string;
  questions: string;
};

function toDraft(form: InviteForm): SettingsDraft {
  return {
    enabled: form.enabled,
    capacity_heads: form.capacity_heads ? String(form.capacity_heads) : "",
    capacity_invites: form.capacity_invites ? String(form.capacity_invites) : "",
    hold_hours: String(form.hold_hours),
    reminder_hours: String(form.reminder_hours),
    application_prefix: form.application_prefix,
    solo_tier_id: form.solo_tier_id ?? "",
    couple_tier_id: form.couple_tier_id ?? "",
    closes_at_ist: form.closes_at_ist ?? "",
    config: form.config,
    details: form.config.details.join("\n"),
    confirmations: form.config.confirmations.join("\n"),
    questions: JSON.stringify(form.config.questions, null, 2),
  };
}

function lines(text: string): string[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

function toPatch(d: SettingsDraft): InviteFormPatch {
  let questions: InviteQuestion[];
  try {
    questions = JSON.parse(d.questions || "[]");
    if (!Array.isArray(questions)) throw new Error();
  } catch {
    throw new Error("Questions must be a JSON list. Check the Questions box.");
  }
  const num = (v: string) => (v.trim() ? Number(v) : null);
  return {
    enabled: d.enabled,
    capacity_heads: num(d.capacity_heads),
    capacity_invites: num(d.capacity_invites),
    hold_hours: Number(d.hold_hours) || 24,
    reminder_hours: Number(d.reminder_hours) || 18,
    application_prefix: d.application_prefix.trim().toUpperCase(),
    solo_tier_id: d.solo_tier_id || null,
    couple_tier_id: d.couple_tier_id || null,
    closes_at_ist: d.closes_at_ist,
    config: {
      ...d.config,
      details: lines(d.details),
      confirmations: lines(d.confirmations),
      questions,
    },
  };
}

const Text: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  type?: string;
}> = ({ label, value, onChange, multiline, rows = 3, placeholder, type = "text" }) => (
  <label className="eventos-field invites-settings__field">
    <span className="eventos-field-label">{label}</span>
    {multiline ? (
      <textarea className="eventos-input" rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className="eventos-input" type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    )}
  </label>
);

const Check: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <label className="invites-settings__check">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

const InviteSettings: React.FC<{ form: InviteForm; eventId: string; onSaved: (f: InviteForm) => void }> = ({
  form,
  eventId,
  onSaved,
}) => {
  const { token } = useAuth();
  const [d, setD] = useState<SettingsDraft>(() => toDraft(form));
  useEffect(() => setD(toDraft(form)), [form]);
  const setC = <K extends keyof InviteFormConfig>(key: K, value: InviteFormConfig[K]) =>
    setD((prev) => ({ ...prev, config: { ...prev.config, [key]: value } }));
  const setM = (key: keyof InviteFormConfig["messages"], value: string) =>
    setD((prev) => ({ ...prev, config: { ...prev.config, messages: { ...prev.config.messages, [key]: value } } }));

  const save = useMutation({
    mutationFn: () => invitesApi.updateForm(eventId, toPatch(d), token ?? undefined),
    onSuccess: (f) => {
      onSaved(f);
      toast.success("Invite form saved");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Save failed"),
  });
  const regenerate = useMutation({
    mutationFn: () => invitesApi.regenerateLink(eventId, token ?? undefined),
    onSuccess: (f) => {
      onSaved(f);
      toast.success("New link created. The old one no longer works.");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't create a new link"),
  });

  const tierOptions = (
    <>
      <option value="">Not set</option>
      {form.tiers.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name} · ₹{t.price.toLocaleString("en-IN")}
          {t.online ? "" : " (not on Zoniq Ticketing yet)"}
        </option>
      ))}
    </>
  );

  return (
    <div className="invites-settings">
      <div className="invites-settings__group">
        <h3>Link &amp; capacity</h3>
        <Check label="Form is live (off = the link shows “not found”)" checked={d.enabled} onChange={(v) => setD({ ...d, enabled: v })} />
        <div className="invites-settings__grid">
          <Text label="Capacity (heads)" type="number" value={d.capacity_heads} onChange={(v) => setD({ ...d, capacity_heads: v })} placeholder="No cap" />
          <Text label="Capacity (invitations)" type="number" value={d.capacity_invites} onChange={(v) => setD({ ...d, capacity_invites: v })} placeholder="No cap" />
          <Text label="Pay link held for (hours)" type="number" value={d.hold_hours} onChange={(v) => setD({ ...d, hold_hours: v })} />
          <Text label="Reminder after (hours)" type="number" value={d.reminder_hours} onChange={(v) => setD({ ...d, reminder_hours: v })} />
          <Text label="Request number prefix" value={d.application_prefix} onChange={(v) => setD({ ...d, application_prefix: v })} />
          <Text label="Waitlist-only from (IST)" type="datetime-local" value={d.closes_at_ist} onChange={(v) => setD({ ...d, closes_at_ist: v })} />
        </div>
        <button type="button" className="eventos-nav-btn" disabled={regenerate.isPending} onClick={() => {
          if (window.confirm("Create a new link? Anyone with the current link won't be able to apply.")) regenerate.mutate();
        }}>
          New link
        </button>
      </div>

      <div className="invites-settings__group">
        <h3>Payment</h3>
        <p className="eventos-subtle">
          Approved guests pay for one of these tickets through Zoniq Ticketing. Add the tickets on the Tickets tab, then run Sell on Zoniq.
        </p>
        <div className="invites-settings__grid">
          <label className="eventos-field invites-settings__field">
            <span className="eventos-field-label">Solo ticket</span>
            <select className="eventos-input" value={d.solo_tier_id} onChange={(e) => setD({ ...d, solo_tier_id: e.target.value })}>
              {tierOptions}
            </select>
          </label>
          <label className="eventos-field invites-settings__field">
            <span className="eventos-field-label">With plus-one ticket</span>
            <select className="eventos-input" value={d.couple_tier_id} onChange={(e) => setD({ ...d, couple_tier_id: e.target.value })}>
              {tierOptions}
            </select>
          </label>
        </div>
      </div>

      <div className="invites-settings__group">
        <h3>Page</h3>
        <div className="invites-settings__grid">
          <label className="eventos-field invites-settings__field">
            <span className="eventos-field-label">Theme</span>
            <select className="eventos-input" value={d.config.theme} onChange={(e) => setC("theme", e.target.value as InviteFormConfig["theme"])}>
              <option value="neon">Neon (black, pink, purple)</option>
              <option value="gold">Gold (dark, champagne gold)</option>
            </select>
          </label>
          <Text label="Brand mark" value={d.config.brand} onChange={(v) => setC("brand", v)} />
          <Text label="Logo image URL (optional)" value={d.config.logo_url ?? ""} onChange={(v) => setC("logo_url", v || null)} />
          <Text label="Event name in messages" value={d.config.display_name ?? ""} onChange={(v) => setC("display_name", v || null)} />
          <Text label="Browser tab title" value={d.config.page_title} onChange={(v) => setC("page_title", v)} />
          <Text label="Headline" value={d.config.headline} onChange={(v) => setC("headline", v)} />
          <Text label="Tagline" value={d.config.tagline ?? ""} onChange={(v) => setC("tagline", v || null)} />
          <Text label="Subhead" value={d.config.subhead} onChange={(v) => setC("subhead", v)} />
          <Text label="Details (one per line)" multiline value={d.details} onChange={(v) => setD({ ...d, details: v })} />
          <Text label="Venue note" value={d.config.venue_note ?? ""} onChange={(v) => setC("venue_note", v || null)} />
          <Text label="Submit button" value={d.config.submit_label} onChange={(v) => setC("submit_label", v)} />
          <Text label="Privacy line" value={d.config.privacy_line} onChange={(v) => setC("privacy_line", v)} />
          <Text label="Done: title" value={d.config.success_title} onChange={(v) => setC("success_title", v)} />
          <Text label="Done: text" value={d.config.success_text} onChange={(v) => setC("success_text", v)} />
          <Text label="At capacity: banner" multiline rows={2} value={d.config.closed_text} onChange={(v) => setC("closed_text", v)} />
          <Text label="Waitlist done: text" value={d.config.waitlist_success_text} onChange={(v) => setC("waitlist_success_text", v)} />
        </div>
      </div>

      <div className="invites-settings__group">
        <h3>Questions</h3>
        <div className="invites-settings__checks">
          <Check label="Ask who invited them" checked={d.config.ask_invited_by} onChange={(v) => setC("ask_invited_by", v)} />
          <Check label="Ask what they do" checked={d.config.ask_occupation} onChange={(v) => setC("ask_occupation", v)} />
          <Check label="Allow a plus-one" checked={d.config.allow_couple} onChange={(v) => setC("allow_couple", v)} />
        </div>
        <div className="invites-settings__grid">
          <Text label="Solo option" value={d.config.solo_label} onChange={(v) => setC("solo_label", v)} />
          <Text label="Plus-one option" value={d.config.couple_label} onChange={(v) => setC("couple_label", v)} />
          <Text label="Plus-one called" value={d.config.partner_label} onChange={(v) => setC("partner_label", v)} />
          <Text label="Questions section title" value={d.config.questions_title} onChange={(v) => setC("questions_title", v)} />
        </div>
        <Text
          label='Extra questions (JSON: [{"id":"song","label":"…","type":"text|textarea|choice","required":true,"max_length":120,"options":[{"value":"a","label":"A"}]}])'
          multiline
          rows={8}
          value={d.questions}
          onChange={(v) => setD({ ...d, questions: v })}
        />
        <Text label="Confirmations (one per line, all must be ticked)" multiline value={d.confirmations} onChange={(v) => setD({ ...d, confirmations: v })} />
        <Text label="WhatsApp consent line" value={d.config.whatsapp_consent} onChange={(v) => setC("whatsapp_consent", v)} />
      </div>

      <div className="invites-settings__group">
        <h3>Messages sent by hand</h3>
        <p className="eventos-subtle">
          Used for the WhatsApp buttons until these templates are approved. Placeholders: {"{first_name} {event} {pay_link} {expiry} {app_no}"}
        </p>
        <Text label="Approved" multiline value={d.config.messages.approved} onChange={(v) => setM("approved", v)} />
        <Text label="Reminder" multiline value={d.config.messages.reminder} onChange={(v) => setM("reminder", v)} />
        <Text label="Waitlisted" multiline value={d.config.messages.waitlisted} onChange={(v) => setM("waitlisted", v)} />
      </div>

      <div className="invites-settings__actions">
        <button type="button" className="eventos-primary-btn" disabled={save.isPending} onClick={() => {
          try {
            toPatch(d);
          } catch (err) {
            toast.error(err instanceof Error ? err.message : "Check the form");
            return;
          }
          save.mutate();
        }}>
          {save.isPending ? "Saving…" : "Save invite form"}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

const EventInvitesTab: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"" | InviteStatus>("");
  const [inviterFilter, setInviterFilter] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const formQuery = useQuery<InviteForm>({
    queryKey: eventId ? queryKeys.inviteForm(eventId) : ["invites", "idle"],
    queryFn: () => invitesApi.getForm(eventId!, token ?? undefined),
    enabled: Boolean(eventId && token),
  });
  const requestsQuery = useQuery<InviteRequestList>({
    queryKey: eventId ? queryKeys.inviteRequests(eventId) : ["invites", "idle-requests"],
    queryFn: () => invitesApi.listRequests(eventId!, token ?? undefined),
    enabled: Boolean(eventId && token),
    refetchInterval: 30_000,
  });

  const refresh = () => {
    if (!eventId) return;
    void queryClient.invalidateQueries({ queryKey: ["invites", eventId] });
  };

  const approve = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) => invitesApi.approve(eventId!, id, force, token ?? undefined),
    onSuccess: (req) => {
      toast.success(`${req.application_no} approved. Pay link held until ${formatWhen(req.hold_expires_at)}.`);
      refresh();
    },
    onError: (err, vars) => {
      const message = err instanceof Error ? err.message : "Couldn't approve";
      if (err instanceof ApiError && err.status === 409 && message.includes("past capacity") && !vars.force) {
        if (window.confirm(`${message}\n\nApprove anyway?`)) approve.mutate({ id: vars.id, force: true });
        return;
      }
      toast.error(message);
    },
  });
  const waitlist = useMutation({
    mutationFn: (id: string) => invitesApi.waitlist(eventId!, id, token ?? undefined),
    onSuccess: (req) => {
      toast.success(`${req.application_no} waitlisted`);
      refresh();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't waitlist"),
  });
  const exportCsv = useMutation({
    mutationFn: (status: string) => invitesApi.exportCsv(eventId!, status, token ?? undefined),
    onSuccess: (blob, status) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invites-${status}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Export failed"),
  });

  const form: InviteForm | undefined = formQuery.data;
  const list: InviteRequestList | undefined = requestsQuery.data;
  const summary = list?.summary ?? form?.summary;
  const requests = useMemo(() => {
    const needle = inviterFilter.trim().toLowerCase();
    return (list?.requests ?? []).filter(
      (r) =>
        (!statusFilter || r.status === statusFilter) &&
        (!needle || (r.invited_by || "").toLowerCase().includes(needle) || (r.ref_code || "").toLowerCase() === needle)
    );
  }, [list, statusFilter, inviterFilter]);
  const questionLabels = useMemo(
    () => Object.fromEntries((form?.config.questions ?? []).map((q) => [q.id, q.label])),
    [form]
  );

  if (!eventId) return <div className="eventos-empty">Open an event to manage invites.</div>;
  if (formQuery.isLoading) return <div className="eventos-empty">Loading invites…</div>;
  if (formQuery.isError || !form) {
    return <div className="eventos-empty">{formQuery.error instanceof Error ? formQuery.error.message : "Couldn't load invites."}</div>;
  }

  const capacityLine =
    summary?.capacity_heads != null
      ? `${summary.spots_left_heads ?? 0} of ${summary.capacity_heads} heads left`
      : summary?.capacity_invites != null
        ? `${summary.spots_left_invites ?? 0} of ${summary.capacity_invites} invitations left`
        : "No cap set";

  return (
    <div className="invites">
      <div className="invites__head">
        <div>
          <h2 className="invites__title">Invite requests</h2>
          <p className="eventos-subtle">
            Guests apply on the link below. Approve to send a personal pay link held for {form.hold_hours} hours. Nobody is
            rejected; anyone not approved is waitlisted.
          </p>
        </div>
        <div className="invites__link-row">
          <input className="eventos-input invites__link" readOnly value={form.public_url} onFocus={(e) => e.target.select()} />
          <button type="button" className="eventos-nav-btn" onClick={() => copy(form.public_url, "Link")}>Copy</button>
          <a className="eventos-nav-btn" href={form.public_url} target="_blank" rel="noreferrer">Open</a>
        </div>
      </div>

      {!form.enabled ? <p className="invites__notice">The form is off. The public link shows “not found” until you turn it on.</p> : null}
      {!form.payments_ready ? (
        <p className="invites__notice">
          Approvals are paused until payment is set up: add the ticket(s) on the Tickets tab, run Sell on Zoniq, then pick
          them under Form settings → Payment. Requests keep coming in meanwhile.
        </p>
      ) : null}

      {summary ? (
        <div className="app-shell-kpi-strip invites__kpis">
          <div className="app-shell-kpi"><span>Paid heads</span><strong>{summary.paid_heads}</strong></div>
          <div className="app-shell-kpi"><span>Holding a pay link</span><strong>{summary.held_heads}</strong></div>
          <div className="app-shell-kpi"><span>Spots left</span><strong>{capacityLine}</strong></div>
          <div className="app-shell-kpi"><span>Pending review</span><strong>{summary.pending}</strong></div>
          <div className="app-shell-kpi"><span>Waitlisted</span><strong>{summary.waitlisted}</strong></div>
        </div>
      ) : null}

      <div className="invites__toolbar">
        <select className="eventos-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "" | InviteStatus)}>
          <option value="">All statuses</option>
          {(Object.keys(STATUS_LABEL) as InviteStatus[]).map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
        <input
          className="eventos-input"
          placeholder="Invited by…"
          value={inviterFilter}
          onChange={(e) => setInviterFilter(e.target.value)}
        />
        <span className="invites__spacer" />
        <button type="button" className="eventos-nav-btn" onClick={() => exportCsv.mutate("paid")}>Export paid (gate)</button>
        <button type="button" className="eventos-nav-btn" onClick={() => exportCsv.mutate("all")}>Export all</button>
        <button type="button" className={`eventos-nav-btn${showSettings ? " is-active" : ""}`} onClick={() => setShowSettings((v) => !v)}>
          Form settings
        </button>
      </div>

      {showSettings ? (
        <InviteSettings
          form={form}
          eventId={eventId}
          onSaved={(f) => {
            queryClient.setQueryData(queryKeys.inviteForm(eventId), f);
            refresh();
          }}
        />
      ) : null}

      <div className="invites__table-wrap">
        <table className="app-shell-table invites__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Guest</th>
              <th>Instagram</th>
              <th>Invited by</th>
              <th>What they do</th>
              <th>Attending</th>
              <th>Status</th>
              <th><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {requestsQuery.isLoading ? (
              <tr><td colSpan={8} className="invites__empty">Loading…</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={8} className="invites__empty">No requests{statusFilter || inviterFilter ? " match these filters" : " yet"}.</td></tr>
            ) : (
              requests.map((r) => {
                const message = messageFor(r);
                const canApprove = r.status === "pending" || r.status === "waitlisted" || r.status === "expired";
                const canWaitlist = r.status === "pending" || r.status === "approved" || r.status === "expired";
                const open = expanded === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`invites__row invites__row--${r.status}`}>
                      <td className="invites__no">{r.application_no}</td>
                      <td>
                        <button type="button" className="invites__name" onClick={() => setExpanded(open ? null : r.id)}>
                          {r.name}
                        </button>
                        <div className="invites__sub">{r.phone}</div>
                      </td>
                      <td>
                        {r.instagram_url ? <a href={r.instagram_url} target="_blank" rel="noreferrer">@{r.instagram}</a> : "—"}
                      </td>
                      <td>{r.invited_by || "—"}</td>
                      <td>{r.occupation || (r.waitlist_only ? <em>waitlist form</em> : "—")}</td>
                      <td>
                        {r.attending === "couple" ? (
                          <>
                            +1 {r.partner_name}
                            {r.partner_instagram_url ? (
                              <div className="invites__sub">
                                <a href={r.partner_instagram_url} target="_blank" rel="noreferrer">@{r.partner_instagram}</a>
                              </div>
                            ) : null}
                          </>
                        ) : (
                          "Solo"
                        )}
                      </td>
                      <td>
                        <span className={`invites__pill invites__pill--${r.status}`}>{STATUS_LABEL[r.status]}</span>
                        {r.status === "approved" && r.hold_expires_at ? (
                          <div className="invites__sub">until {formatWhen(r.hold_expires_at)}</div>
                        ) : null}
                        {r.status === "paid" && r.paid_at ? <div className="invites__sub">{formatWhen(r.paid_at)}</div> : null}
                      </td>
                      <td className="invites__actions">
                        {canApprove ? (
                          <button
                            type="button"
                            className="eventos-primary-btn"
                            disabled={approve.isPending || !form.payments_ready}
                            title={form.payments_ready ? undefined : "Set up payment first (Form settings → Payment)"}
                            onClick={() => approve.mutate({ id: r.id, force: false })}
                          >
                            {r.status === "expired" ? "Re-approve" : "Approve"}
                          </button>
                        ) : null}
                        {canWaitlist ? (
                          <button type="button" className="eventos-nav-btn" disabled={waitlist.isPending} onClick={() => waitlist.mutate(r.id)}>
                            Waitlist
                          </button>
                        ) : null}
                        <a className="eventos-nav-btn" href={waLink(r.phone, message)} target="_blank" rel="noreferrer">
                          WhatsApp
                        </a>
                        {r.pay_url && r.status === "approved" ? (
                          <button type="button" className="eventos-nav-btn" onClick={() => copy(r.pay_url!, "Pay link")}>
                            Copy pay link
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {open ? (
                      <tr className="invites__detail">
                        <td colSpan={8}>
                          <dl>
                            <dt>Email</dt><dd>{r.email || "—"}</dd>
                            <dt>Came from</dt><dd>{r.utm_source || "—"}{r.ref_code ? ` · ref ${r.ref_code}` : ""}</dd>
                            <dt>Requested</dt><dd>{formatWhen(r.created_at)}</dd>
                            <dt>WhatsApp updates</dt><dd>{r.whatsapp_opt_in ? "Yes" : "No"}</dd>
                            {Object.entries(r.answers).map(([k, v]) => (
                              <React.Fragment key={k}>
                                <dt>{questionLabels[k] || k}</dt>
                                <dd>{v}</dd>
                              </React.Fragment>
                            ))}
                          </dl>
                          {message ? (
                            <div className="invites__message">
                              <p>{message}</p>
                              <button type="button" className="eventos-nav-btn" onClick={() => copy(message, "Message")}>Copy message</button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventInvitesTab;
